import React, { useEffect } from "react";
import { Drawer, List, Typography, Avatar, Spin } from "antd";
import { Notification, useNotificationStore } from "../redux/notification.store";
import { FaDownload, FaFileAlt } from "react-icons/fa";
import { FormattedMessage } from "react-intl";

const { Text, Link } = Typography;

interface DownloadRequestProgramDetailsAttributes {
    Status: string;
    DownloadRequestId: number;
    UniversityName: string;
    FileUrl: string;
}

interface ParsedNotification extends Omit<Notification, "paramsJSON"> {
    params: any;
}

interface NotificationDrawerProps {
    open: boolean;
    onClose: () => void;
    notifications: Notification[];
    notificationUnread: Notification[];
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
    open,
    onClose,
    notifications,
    notificationUnread
}) => {
    const { read } = useNotificationStore();
    
    const parsedNotifications: ParsedNotification[] = notifications.map((n) => ({
        ...n,
        params: safeParseJSON(n.parameters),
    }));

    useEffect(() => {
        if (open) {
            if (notificationUnread.length > 0) {
                read(notificationUnread.map(x => x.id));
            }
        }
    }, [open, notificationUnread, read])

    return (
        <Drawer
            title={<FormattedMessage id={"app.notification.title"} />}
            placement="right"
            onClose={onClose}
            open={open}
            width={400}
            styles={{ body: { padding: 0 } }}
        >
            <List
                dataSource={parsedNotifications}
                renderItem={(item) => (
                    <List.Item style={{ padding: 0 }}>
                        <List.Item.Meta
                            description={
                                <div style={{ display: 'flex', padding: 16, gap: 12, alignItems: 'center' }}>
                                    <Avatar size={64} icon={<FaFileAlt />} />
                                    <div style={{ flex: 1 }}>
                                        <div>
                                            <Text style={{ fontWeight: 'bold' }}><FormattedMessage id={"app.notification.download_program"} /></Text>
                                            <br />
                                            <Text style={{ color: "#527aa1" }}>{item.createdBy?.name}</Text>
                                        </div>
                                        {item.type === 0 && item.params && (
                                            <DownloadRequest params={item.params} date={new Date(item.createdUtcDate).toLocaleString()} />
                                        )}
                                    </div>
                                    {item.type === 0 && item.params && (
                                        <DownloadButtonRequest params={item.params} />
                                    )}
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        </Drawer>
    );
};

const DownloadRequest = ({ params, date }: { params: DownloadRequestProgramDetailsAttributes, date: string }) => {
    return <div>
        <Text style={{ color: "#527aa1" }}>{params.UniversityName}</Text>
        <br />
        <Text style={{ color: "#527aa1" }}>{date}</Text>
    </div>
}

const DownloadButtonRequest = ({ params }: { params: DownloadRequestProgramDetailsAttributes }) => {
    return <div>
        {params.Status === "ready" && params.FileUrl && (
            <Link
                href={params.FileUrl}
                target="_blank"
                rel="noopener noreferrer"
            >
                <FaDownload fontSize={24} />
            </Link>
        )}
        {params.Status === "loading" && <Spin spinning />}
    </div>
}

function safeParseJSON(json: string): any {
    try {
        return JSON.parse(json);
    } catch {
        return {};
    }
}

export default NotificationDrawer;
